<script setup lang="ts">
import winePlaceholder from '@/assets/images/wine_placeholder.png'
import { useWineStore } from '@/stores/wine'
import { ScannedWine } from '@/types'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

// Props from route
interface Props {
    id: string // This is now scanId
}

const props = defineProps<Props>()
const router = useRouter()
const route = useRoute()
const wineStore = useWineStore()

// Check if in read-only mode
const isReadOnly = computed(() => route.query.readOnly === 'true')
const scanId = computed(() => props.id) // scanId comes from params.id

// Form state
const wineRating = ref(0)
const reviewPlace = ref('')
const reviewComment = ref('')
const isSubmitting = ref(false)

// Scan data - now the main data source
const scanData = ref<ScannedWine | null>(null)
const isLoading = ref(false)

// Wine display data - all from scan data
const wineName = computed(() => scanData.value?.wine?.name || '')
// const wineWinemaker = computed(() => scanData.value?.wine?.winemaker?.name + ' ' || '')
const wineYear = computed(() => ', ' + scanData.value?.wine?.year || '')

const wineCirculation = computed(() => {
    if (!scanData.value) return ''
    const total = scanData.value.bottle_total
    const serialNo = scanData.value.bottle.serial_no
    return total ? `Item №${serialNo} out of ${total}` : ''
})

const bottleImage = computed(() => scanData.value?.wine?.bottle_image?.url || winePlaceholder)

// Check if we have scan data
const hasScanData = computed(() => !!scanData.value?.wine)

// Background style for wine review top section
const topBackgroundStyle = computed(() => {
    const bgImage = scanData.value?.wine?.bg_image?.url
    return {
        background: bgImage
            ? `linear-gradient(rgba(0, 0, 0, .2), rgba(0, 0, 0, 0)), url(${bgImage})`
            : '#d48d8d',
        backgroundSize: 'cover',
        backgroundPosition: 'center top',
    }
})

// Existing review data from scan
const existingRating = computed(() => scanData.value?.review?.rating || 0)

// Load scan data on mount
onMounted(async () => {
    if (!scanId.value) {
        router.push('/')
        return
    }

    try {
        isLoading.value = true
        const result = await wineStore.loadScan(scanId.value)

        if (result.success && result.data) {
            scanData.value = result.data

            // Fill form with existing review data from scan
            const review = result.data.review
            if (review) {
                wineRating.value = review.rating
                reviewPlace.value = review.place || ''
                reviewComment.value = review.comment || ''
            }
        } else {
            router.push('/')
        }
    } catch (error) {
        console.error('Failed to load scan:', error)
        router.push('/')
    } finally {
        isLoading.value = false
    }
})

const handleRatingChange = (rating: number) => {
    wineRating.value = rating
}

const submitReview = async () => {
    if (!scanData.value?.wine || wineRating.value === 0) {
        console.log('❌ Cannot submit: missing scan data or rating', {
            scanData: !!scanData.value,
            rating: wineRating.value,
        })
        return
    }

    try {
        isSubmitting.value = true

        const reviewData = {
            wine_id: scanData.value.wine.id,
            rating: wineRating.value,
            comment: reviewComment.value.trim() || undefined,
            place: reviewPlace.value.trim() || undefined,
        }

        console.log('📝 Submitting review:', reviewData)
        await wineStore.createReview(reviewData)
        console.log('✅ Review submitted successfully')

        // Navigate back to scan detail
        router.push(`/wine_info/${scanId.value}`)
    } catch (error) {
        console.error('❌ Failed to submit review:', error)
        alert(`Failed to submit review: ${error}`)
        // TODO: Show error notification
    } finally {
        isSubmitting.value = false
    }
}

const goBackToWineInfo = () => {
    if (scanId.value) {
        router.push(`/wine_info/${scanId.value}`)
    } else {
        router.push('/')
    }
}
</script>

<template>
    <div class="wine-review">
        <!-- Loading state -->
        <div v-if="isLoading" class="wine-review__loading">
            <div class="loading-spinner">Loading...</div>
        </div>

        <!-- Content -->
        <template v-else-if="hasScanData">
            <div class="wine-review__top" :style="topBackgroundStyle">
                <div class="wine-review__name">
                    <span>{{ wineName }} </span>{{ wineYear }}
                </div>
                <div class="wine-review__border"></div>
                <div class="wine-review__circulation">{{ wineCirculation }}</div>
            </div>
            <div class="wine-review__info">
                <div class="wine-review__text-wrap wine-review__text-wrap_with-bottle">
                    <div class="wine-review__title">
                        {{
                            isReadOnly
                                ? 'Your rating'
                                : existingRating
                                  ? 'Change rating'
                                  : 'Rate it'
                        }}
                    </div>
                    <div class="wine-review__rating">
                        <RatingStars
                            :isCreate="true"
                            :rating="wineRating"
                            size="large"
                            :interactive="!isReadOnly"
                            @change="handleRatingChange"
                        />
                    </div>
                    <div class="wine-review__bottle-wrap">
                        <img :src="bottleImage" alt="" class="wine-review__bottle" />
                    </div>
                </div>
                <div class="wine-review__text-wrap">
                    <div class="wine-review__title">
                        {{ isReadOnly ? 'Purchase location' : 'Where the purchase was made?' }}
                    </div>
                    <input
                        v-model="reviewPlace"
                        class="wine-review__input"
                        type="text"
                        placeholder="Укажите место"
                        :disabled="isReadOnly"
                    />
                </div>
                <div class="wine-review__text-wrap">
                    <div class="wine-review__title">
                        {{ isReadOnly ? 'Your opinion' : 'Share your opinion' }}
                    </div>
                    <textarea
                        v-model="reviewComment"
                        class="wine-review__input"
                        placeholder="Review"
                        rows="3"
                        :disabled="isReadOnly"
                    />
                </div>

                <div class="wine-review__actions">
                    <button v-if="isReadOnly" class="btn" @click="goBackToWineInfo">
                        Посмотреть информацию о вине
                    </button>
                    <button
                        v-else
                        class="btn"
                        :class="{ 'btn--loading': isSubmitting }"
                        :disabled="isSubmitting || wineRating === 0"
                        @click="submitReview"
                    >
                        {{
                            isSubmitting
                                ? 'Sending...'
                                : existingRating
                                  ? 'Update review'
                                  : 'Submit a review'
                        }}
                    </button>
                </div>
            </div>
        </template>

        <!-- Error state -->
        <div v-else class="wine-review__error">
            <p>Product not found</p>
            <button class="btn" @click="$router.push('/')">Home</button>
        </div>
    </div>
</template>

<style lang="stylus">

.wine-review
    &__loading
        display flex
        align-items center
        justify-content center
        min-height 200px
        font-size 16px
        color var(--app-text-grey-color)
    &__error
        display flex
        flex-direction column
        align-items center
        justify-content center
        min-height 200px
        gap 16px
        padding 16px
        text-align center
        p
            font-size 16px
            color var(--app-text-color)
    &__input
        border-radius 8px
        border 1px solid var(--app-border-color)
        background-color var(--app-bg-secondary)
        padding 12px
        font-size 14px
        line-height 20px
        font-weight 400
        color var(--app-text-color)
        font-family 'Involve', sans-serif
        &::placeholder
            font-family 'Involve', sans-serif
            font-size 14px
            line-height 20px
            font-weight 400
            color var(--app-text-grey-color)
        &:focus
            outline none
            border-color var(--app-main-accent-color)
            &::placeholder
                color transparent
    &__border
        width 100%
        height 1px
        margin 12px 0
        background-color rgba(245, 245, 245, 0.5)
    &__text-wrap
        display flex
        flex-direction column
        gap 8px
        padding-bottom 24px
        &_with-bottle
            position relative
            padding-right 112px
    &__title
        font-size 20px
        line-height 26px
        font-weight 600
        color var(--app-text-color)
    &__actions
        padding 8px 0 20px
    //     z-index 10
    //     display flex
    //     flex-direction column
    //     gap 12px
    //     background var(--app-bg-color)
    &__info
        z-index 10
        position relative
        background var(--app-bg-color)
        border-radius 16px 16px 0 0
        margin-top -20px
        padding 32px 16px 0
    &__top
        position relative
        z-index 1
        padding 24px 112px 56px 16px
        background var(--app-accent-light)
    &__name
        margin 0 auto
        font-size 24px
        line-height 28px
        font-weight 400
        color var(--app-text-light-color)
        text-align left
    &__circulation
        font-size 13px
        line-height 17px
        font-weight 400
        color var(--app-text-light-color)
        text-align left
    &__bottle
        object-fit contain
        width 100%
        height auto
        &-wrap
            position absolute
            right -16px
            bottom 8px
            display flex
            justify-content center
            width 114px
            height 270px
            padding 0 18px
</style>
